import AbstractShape from "./AbstractShape";
import Coordinate from "../support/Coordinate";
import EllipseBorder from "../support/EllipseBorder";
import SmoothCanopyStyle from "./canopyStyles/SmoothCanopyStyle";
import LobedCanopyStyle from "./canopyStyles/LobedCanopyStyle";
import ConiferCanopyStyle from "./canopyStyles/ConiferCanopyStyle";

const CANOPY_STYLES = {
    smooth: SmoothCanopyStyle,
    lobed: LobedCanopyStyle,
    conifer: ConiferCanopyStyle,
};

export default class Ellipse extends AbstractShape {
    selected = false;
    isHovered = false;
    type = 'ellipse';

    constructor(provider, data, appearance, speciesIds, label) {
        super(provider, data, appearance, speciesIds, label);

        this.absX = data.x;
        this.absY = data.y;
        this.absSize = data.width;
        this.canopySeed = data.canopySeed ?? null;
        this.center = new Coordinate(provider, this.absX, this.absY);
        this.border = new EllipseBorder(provider, this, this.center, this.absSize);

        this._canopyStyle = data.canopyStyle ?? 'smooth';
        this._style = this._createStyle(this._canopyStyle);
    }

    _createStyle(type) {
        const StyleClass = CANOPY_STYLES[type] ?? SmoothCanopyStyle;
        return new StyleClass(this);
    }

    get canopyStyle() {
        return this._canopyStyle;
    }

    set canopyStyle(value) {
        this._canopyStyle = value;
        this._style = this._createStyle(value);
    }

    // Regenerates canopy points using the current seed (or a new one if none set).
    generateCanopyPoints() {
        this._style.clearPoints();
        this._style.ensurePoints();
    }

    display(g) {
        const opacity = this.p.map(this.currentSize, 500, 1500, 1, 0.25, true);

        this.displayHighlight(g);
        this.appearance.apply(g, opacity);
        this._style.display(g, opacity);
        this.labels.display(g, this.currentSize);
    }

    displayHighlight(g) {
        if (!this.highlight) return;
        const r = this.currentSize / 2;
        this.appearance.drawOuterHighlight(g, this.highlight, (ctx) => {
            ctx.ellipse(this.currentX, this.currentY, r, r, 0, 0, Math.PI * 2);
        });
    }

    move(dx, dy) {
        this.absX += dx;
        this.absY += dy;
        this.center.move(dx, dy);
    }

    setPosition(x, y) {
        let change = [x - this.absX, y - this.absY];
        this.absX = x;
        this.absY = y;
        return change;
    }

    hovered() {
        var d = this.p.dist(this.currentX, this.currentY, this.view.currentX, this.view.currentY);
        return d < this.currentSize / 2 + this.appearance.scaledStrokeWidth / 2;
    }

    hoveredWithSupport() {
        return (this.border.hovered() ? this.border : null)
            || (this.labels.hovered() ? this.labels : null)
            || (this.hovered() ? this : null);
    }

    hover() {
        this.isHovered = true;
    }

    unhover() {
        this.isHovered = false;
    }

    clone(dx, dy) {
        return new Ellipse(
            this.provider,
            {
                x: this.absX + dx,
                y: this.absY + dy,
                width: this.absSize,
                canopyStyle: this._canopyStyle,
                canopySeed: null,
                layer: this.layer?.id,
                addedDate: this.addedDate,
                removedDate: this.removedDate,
            },
            this.appearance.clone(),
            this.speciesIds,
            this.labels.clone(),
        );
    }

    surrounded(minX, minY, maxX, maxY) {
        return (this.absX - this.absSize / 2) >= minX
            && (this.absX + this.absSize / 2) <= maxX
            && (this.absY - this.absSize / 2) >= minY
            && (this.absY + this.absSize / 2) <= maxY;
    }

    mouseOver() {
        if (!this.selected) {
            if (this.hovered()) {
                this.cursor.pointer();
                return true;
            }
            return false;
        }

        if (this.selection.length > 1) {
            if (this.hovered()) {
                this.cursor.grab();
                return true;
            }
            return false;
        }

        if (this.border.mouseOver()) return true;
        if (this.labels.mouseOver()) return true;

        if (this.hovered()) {
            this.cursor.grab();
            return true;
        }
        return false;
    }

    setSize(value) {
        if (value <= 0) return false;
        this.absSize = value;
    }

    getMiniView(max) {
        let maxSize = this.absMaxSize;
        let size = this.p.map(this.size, 0, maxSize, 0, max);
        return this._style.getMiniView(size);
    }

    get size() {
        return this.absSize;
    }

    set size(size) {
        this.absSize = size;
    }

    get currentX() {
        return this.absX * this.view.scale;
    }

    get currentY() {
        return this.absY * this.view.scale;
    }

    get currentSize() {
        return this.absSize * this.view.scale;
    }

    get currentMaxSize() {
        return this.currentSize;
    }

    get absMaxSize() {
        let base = this.absSize + (this.appearance.strokeWidth ?? 0);
        return base * this._style.absMaxSizeMultiplier;
    }

    get absMinX() {
        return this.absX - this.absMaxSize / 2;
    }

    get absMinY() {
        return this.absY - this.absMaxSize / 2;
    }

    get absMaxX() {
        return this.absX + this.absMaxSize / 2;
    }

    get absMaxY() {
        return this.absY + this.absMaxSize / 2;
    }

    get displaySize() {
        return this.calculatePixelToDisplayDistance(this.absSize);
    }

    get displayArea() {
        const r = this.absSize / 2;
        const factor = this.settings.metric ? 100 : 30.48;
        return Math.round(Math.PI * r * r / (factor * factor) * 100) / 100;
    }

    export() {
        return {
            type: 'ellipse',
            data: {
                x: this.absX,
                y: this.absY,
                width: this.absSize,
                height: this.absSize,
                canopyStyle: this._canopyStyle !== 'smooth' ? this._canopyStyle : undefined,
                canopySeed: this.canopySeed ?? undefined,
                notes: this.notes,
                addedDate: this.addedDate,
                removedDate: this.removedDate,
                layer: this.layer?.id,
                ...this.appearance.export(),
                ...this.labels.export(),
            },
            speciesIds: this.speciesIds,
        };
    }
}
